<template>
  <div class="rv-taxpod-details">
    <!-- Intro -->
    <div class="rv-taxpod-details__intro">
      <div class="rv-taxpod-details__icon">
        <v-icon color="var(--app-accent)" size="26">mdi-microsoft-excel</v-icon>
      </div>
      <div>
        <h2 class="rv-taxpod-details__title">HRDC taxPOD Details</h2>
        <p class="rv-taxpod-details__subtitle">
          Export every taxPOD application into one Excel workbook — one row per
          application, with all the information shown on the application View page.
        </p>
      </div>
    </div>

    <!-- Security note -->
    <div class="rv-taxpod-details__note">
      <v-icon size="16" color="var(--app-warning)" class="mr-2">mdi-information-outline</v-icon>
      <span>
        Attachment files are provided as clickable links (bullet list per row).
        For security, these links stay valid for <b>7 days</b> from the time you
        download the file.
      </span>
    </div>

    <!-- Action -->
    <div class="rv-taxpod-details__actions">
      <button
        class="rv-taxpod-details__btn"
        :class="{ 'rv-taxpod-details__btn--loading': isExporting }"
        :disabled="isExporting"
        @click="$emit('exportExcel')"
      >
        <template v-if="isExporting">
          <v-progress-circular indeterminate size="16" width="2" color="#fff" class="mr-2" />
          Preparing file…
        </template>
        <template v-else>
          <v-icon small color="#fff" class="mr-2">mdi-download-outline</v-icon>
          Download Excel
        </template>
      </button>
      <span v-if="isExporting" class="rv-taxpod-details__hint">
        Compiling all applications — this can take up to a minute, please keep this tab open.
      </span>
    </div>
  </div>
</template>

<script>
export default {
  emits: ["exportExcel"],
  props: {
    isExporting: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-taxpod-details__icon {
  background: var(--app-accent-soft);
}

.dark-mode .rv-taxpod-details__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .rv-taxpod-details__note {
  background: var(--app-warning-bg);
}

.dark-mode .rv-taxpod-details__hint {
  color: var(--app-text-muted);
}

/* >>> end auto-dark <<< */

.rv-taxpod-details {
  font-family: 'Lato', sans-serif;
  padding: 24px 28px;
  max-width: 720px;
}

/* Intro */
.rv-taxpod-details__intro {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}

.rv-taxpod-details__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e0f2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rv-taxpod-details__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0 0 4px;
  letter-spacing: -0.2px;
}

.rv-taxpod-details__subtitle {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* Security note */
.rv-taxpod-details__note {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 22px;
  padding: 12px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  font-size: 0.78rem;
  color: #92400e;
  line-height: 1.5;
}

/* Dark mode: the auto-dark block only swaps the note's background, which left
   the light border (#fde68a) glaring over a muddy amber wash and the brown
   text (#92400e) nearly unreadable. Re-tone the whole box. */
.dark-mode .rv-taxpod-details__note {
  border-color: var(--app-warning-border);
  color: var(--app-warning-fg);
}

.dark-mode .rv-taxpod-details__title {
  color: var(--app-text);
}

/* Actions */
.rv-taxpod-details__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.rv-taxpod-details__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 26px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.25);
}

.rv-taxpod-details__btn:hover {
  background: linear-gradient(135deg, #0284c7, #0ea5e9);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
  transform: translateY(-1px);
}

.rv-taxpod-details__btn--loading,
.rv-taxpod-details__btn:disabled {
  cursor: not-allowed;
  opacity: 0.85;
  transform: none;
  box-shadow: none;
}

.rv-taxpod-details__hint {
  font-size: 0.78rem;
  color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .rv-taxpod-details {
    padding: 16px 14px;
    max-width: 100%;
  }
}
</style>
